import { useState } from 'react';
import { Plane, Eye, EyeOff, ArrowLeft, Loader2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../../context/AuthContext';
import { track } from '../../analytics';

interface LoginScreenProps {
  onSwitchToSignUp: () => void;
}

export function LoginScreen({ onSwitchToSignUp }: LoginScreenProps) {
  const { signIn, signInWithApple, resetPassword } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError('Please enter your email/username and password.');
      return;
    }

    setLoading(true);
    const result = await signIn(email.trim(), password);
    setLoading(false);

    if (result.error) {
      setError(result.error);
    } else {
      track('signin', { method: 'email' });
    }
  };

  const handleAppleSignIn = async () => {
    setError(null);
    setLoading(true);
    const result = await signInWithApple();
    setLoading(false);

    if (result.error) {
      setError(result.error);
    } else {
      track('signin', { method: 'apple' });
    }
  };

  if (showForgot) {
    return (
      <ForgotPasswordScreen
        initialEmail={email}
        resetPassword={resetPassword}
        onBack={() => setShowForgot(false)}
      />
    );
  }

  return (
    <div className="min-h-dvh bg-slate-900 flex flex-col items-center justify-center px-6">
      {/* Logo / Title */}
      <div className="flex flex-col items-center mb-10">
        <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-4">
          <Plane size={32} className="text-accent" />
        </div>
        <h1 className="text-2xl font-bold text-slate-100">Rost & Found</h1>
        <p className="text-slate-400 text-sm mt-1">Sign in to your account</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSignIn} className="w-full max-w-sm space-y-4">
        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Email or username */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-400 mb-1.5">
            Email or username
          </label>
          <input
            id="email"
            type="text"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com or @handle"
            className="w-full px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors"
          />
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-slate-400 mb-1.5">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="w-full px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors pr-12"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        {/* Forgot password */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => setShowForgot(true)}
            className="text-xs text-slate-500 hover:text-accent transition-colors"
          >
            Forgot password?
          </button>
        </div>

        {/* Sign In Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-lg bg-accent text-white font-semibold text-base hover:bg-accent-dark active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>

        {/* Apple Sign In */}
        {Capacitor.isNativePlatform() && (
          <button
            type="button"
            onClick={handleAppleSignIn}
            disabled={loading}
            className="w-full py-3 rounded-lg bg-black text-white font-medium text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed border border-slate-600"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M13.1 9.45c-.02-1.95 1.6-2.89 1.67-2.93a3.63 3.63 0 00-2.86-1.54c-1.2-.13-2.38.72-2.99.72-.62 0-1.56-.7-2.57-.68a3.78 3.78 0 00-3.18 1.94c-1.37 2.37-.35 5.86.97 7.78.66.94 1.43 1.99 2.44 1.95 1-.04 1.37-.63 2.57-.63 1.19 0 1.53.63 2.57.61 1.06-.02 1.72-.94 2.36-1.89a7.92 7.92 0 001.08-2.2c-.02-.01-2.06-.79-2.08-3.13zM11.15 3.62A3.7 3.7 0 0012 1a3.77 3.77 0 00-2.43 1.26 3.5 3.5 0 00-.88 2.54 3.12 3.12 0 002.46-1.18z"
                fill="currentColor"
              />
            </svg>
            Sign in with Apple
          </button>
        )}

        {/* Divider */}
        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-700" />
          </div>
        </div>

        {/* Switch to Sign Up */}
        <p className="text-center text-sm text-slate-400">
          Don't have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToSignUp}
            className="text-accent font-medium hover:underline"
          >
            Create one
          </button>
        </p>
      </form>
    </div>
  );
}

// ── Forgot Password Screen ──

function ForgotPasswordScreen({
  initialEmail,
  resetPassword,
  onBack,
}: {
  initialEmail: string;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  onBack: () => void;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    const result = await resetPassword(email.trim());
    setLoading(false);

    if (result.error) {
      setError(result.error);
    } else {
      setSent(true);
    }
  };

  return (
    <div className="min-h-dvh bg-slate-900 flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-300 mb-8"
        >
          <ArrowLeft size={16} />
          Back to sign in
        </button>

        <h2 className="text-xl font-bold text-slate-100 mb-2">Reset your password</h2>
        <p className="text-slate-400 text-sm mb-6">
          {sent
            ? 'Check your inbox for a password reset link.'
            : "Enter your email and we'll send you a reset link."}
        </p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm mb-4">
            {error}
          </div>
        )}

        {sent ? (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-3 text-green-400 text-sm">
            Reset link sent to <span className="font-medium">{email}</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full px-4 py-3 rounded-lg bg-slate-700 border border-slate-600 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg bg-accent text-white font-semibold text-base hover:bg-accent-dark active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
